import { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Stack, Button, Skeleton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";

const SEV_COLOR = {
  high: "#E24B4A", medium: "#eb7434", low: "#EF9F27", info: "#0d82b8",
};
const SEV_ORDER = { high: 0, medium: 1, low: 2, info: 3 };

const SevBadge = ({ label }) => {
  const c = SEV_COLOR[label] ?? "#888888";
  return (
    <Box sx={{
      display: "inline-flex", px: 0.7, py: 0.15,
      bgcolor: `${c}22`, border: `1px solid ${c}`, borderRadius: 0.5,
    }}>
      <Typography sx={{ fontSize: 9, color: c, fontWeight: 700, lineHeight: 1.4 }}>
        {label?.toUpperCase() ?? "—"}
      </Typography>
    </Box>
  );
};

const TiresiaAnomalies = ({ height }) => {
  const navigate = useNavigate();
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.getTiresiaAnomalies({ hours: 24 })
      .then(res => setAnomalies(res.data?.data ?? []))
      .catch(() => setAnomalies([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by source IP → worst severity + anomaly count
  const rows = useMemo(() => {
    const map = new Map();
    anomalies.forEach(a => {
      const ip = a.entity?.id;
      if (!ip || ip === "_global_") return;
      if (!map.has(ip)) map.set(ip, { ip, items: [] });
      map.get(ip).items.push(a);
    });

    return Array.from(map.values())
      .map(({ ip, items }) => {
        const worst = items.reduce((best, a) => {
          const o = SEV_ORDER[a.severity_label] ?? 99;
          return o < best.o ? { o, sev: a.severity_label } : best;
        }, { o: 99, sev: "info" });
        return { ip, severity: worst.sev, count: items.length };
      })
      .sort((a, b) => {
        const ds = (SEV_ORDER[a.severity] ?? 99) - (SEV_ORDER[b.severity] ?? 99);
        return ds !== 0 ? ds : b.count - a.count;
      })
      .slice(0, 12);
  }, [anomalies]);

  return (
    <Box sx={{
      p: 2, bgcolor: "#1e1e1e", borderRadius: "15px",
      height, overflow: "hidden", display: "flex", flexDirection: "column",
    }}>
      {/* ── Header ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>
          Behavioral anomalies
        </Typography>
      </Stack>

      {/* ── Table ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {/* Column headers */}
        <Stack direction="row" alignItems="center"
          sx={{ px: 1, pb: 0.6, borderBottom: "1px solid #2e2e2e" }}>
          <Typography sx={{ flex: 1, fontSize: 10, color: "#555", fontWeight: 700, letterSpacing: 0.5 }}>
            SOURCE IP
          </Typography>
          <Typography sx={{ width: 72, fontSize: 10, color: "#555", fontWeight: 700, letterSpacing: 0.5 }}>
            SEVERITY
          </Typography>
          <Typography sx={{ width: 52, fontSize: 10, color: "#555", fontWeight: 700, textAlign: "right", letterSpacing: 0.5 }}>
            COUNT
          </Typography>
        </Stack>

        {loading ? (
          <Stack spacing={0.5} sx={{ pt: 0.5 }}>
            {[...Array(8)].map((_, i) => (
              <Stack key={i} direction="row" alignItems="center" sx={{ px: 1, py: 0.65 }}>
                <Skeleton variant="text" sx={{ flex: 1, bgcolor: "rgba(255,255,255,0.07)", mr: 1 }} />
                <Skeleton variant="rounded" width={50} height={18} sx={{ bgcolor: "rgba(255,255,255,0.07)", mr: 1 }} />
                <Skeleton variant="text" width={32} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
              </Stack>
            ))}
          </Stack>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#777", py: 3, textAlign: "center" }}>
            No anomalies in the last 24h.
          </Typography>
        ) : (
          rows.map(row => (
            <Stack key={row.ip} direction="row" alignItems="center" sx={{
              px: 1, py: 0.65,
              borderBottom: "1px solid #252525",
              "&:hover": { bgcolor: "#45909510" },
            }}>
              <Typography sx={{
                flex: 1, fontFamily: "monospace", fontSize: 12, color: "#459095",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mr: 1,
              }}>
                {row.ip}
              </Typography>
              <Box sx={{ width: 72 }}>
                <SevBadge label={row.severity} />
              </Box>
              <Typography sx={{
                width: 52, fontSize: 12, fontFamily: "monospace",
                color: "#888", textAlign: "right",
              }}>
                {row.count}
              </Typography>
            </Stack>
          ))
        )}
      </Box>

      {/* ── Footer button ── */}

      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
          onClick={() => {
            const path = sessionStorage.getItem("type") === "reseller"
              ? "/reseller/myC/tiresia-analysis"
              : "/user/tiresia-engine/tiresia-analysis";
            navigate(path, { state: { tab: "1" } });
          }}
          sx={{
            fontSize: 11, textTransform: "none",
            color: "#459095", borderColor: "#459095",
            "&:hover": { bgcolor: "#45909515", borderColor: "#459095" },
          }}
        >
          View in Behaviors
        </Button>
      </Box>
    </Box>
  );
};

export default TiresiaAnomalies;
